// src/lib/cyranoStarters.js
// Cyrano's starter suggestions — the neutral, vertical-free defaults.
//
// WHY THIS FILE EXISTS
// These lists used to sit at the top of CyranoPage.jsx naming one industry's
// market throughout: "a cold CIO pursuit at a mid-market carrier", "best
// practices for first touch emails in P&C insurance", "how to reference a
// Guidewire implementation signal", "why regional carriers are falling behind on
// core system modernization".
//
// For LightSpeed — an MGA writing management liability, whose derived buyer_roles
// are risk manager, insurance broker, general counsel and chief compliance
// officer — those are not merely bland. A carrier is who an MGA places business
// WITH, not who it sells TO, so every suggestion pointed at the wrong side of
// the market. The same defect as the hardcoded core-system vendors that
// useTenantVocabulary replaced, and as exavalu_relevance before migration 0030:
// nothing in the base may carry one customer's market.
//
// THE RULE THESE FOLLOW
// Name the SHAPE of the task, never the industry. "the primary decision-maker"
// rather than "the CIO"; "this account" rather than "this carrier"; "the
// strongest signal loaded" rather than "a Guidewire implementation signal". A
// prompt must not assert a vertical it was not told — the same line
// api/cyrano/system-prompt.js already states about companyDesc.
//
// These remain the fallback once the per-tenant derivation lands: `null` means
// not answered yet and falls back to these, an empty derived list is honoured
// rather than replaced. Same null-vs-empty distinction as useTenantVocabulary.

// Suggestions per outreach type, shown as chips once a type is chosen.
export const STARTER_PROMPTS_BY_TYPE = {
  first_touch: [
    'Draft a first touch email to the primary decision-maker using the strongest signal loaded',
    'Write a cold open that surfaces the problem without stating it directly',
    'Draft a first touch that opens with their situation, not our capabilities',
    'Write a warm intro email — we have a mutual connection at this account',
  ],
  follow_up: [
    'Write a follow-up — no response after 10 days, use a different angle than the first touch',
    'Draft Touch 2 that adds a consequence question the first email did not raise',
    'Write a follow-up that references something new without repeating the first message',
    'Draft a 3-sentence follow-up that creates curiosity without pressure',
  ],
  meeting_request: [
    'Write a meeting request for a 20-minute discovery call — frame it as exploration not a pitch',
    'Draft a meeting ask that names the specific problem we want to explore together',
    'Write a meeting request after a warm introduction — brief, specific, low pressure',
    'Draft a qualifying meeting request that helps us both decide if there is a fit',
  ],
  post_meeting: [
    'Write a post-meeting recap — reflect back what they said, next steps, one forward question',
    'Draft a follow-up for a discovery call where they shared a real problem',
    'Write a post-meeting email that moves them toward articulating what ideal looks like',
    'Draft a recap that confirms the next step without being pushy about the timeline',
  ],
  proposal_follow: [
    'Write a proposal follow-up — not just checking in, one question that moves things forward',
    'Draft a follow-up after submitting a proposal — acknowledge their timeline, ask one thing',
    'Write a check-in after 2 weeks of silence post-proposal without sounding desperate',
    'Draft a follow-up that surfaces whether there are concerns we have not heard yet',
  ],
  exec_intro: [
    'Draft an executive intro using a referral — name-drop in sentence one, one clear ask',
    'Write a warm intro to the budget holder — referred by a colleague after our last meeting',
    'Draft a board-level introduction for an account where we have an internal champion',
    'Write an exec intro that leads with the referrer and lands on one specific question',
  ],
  custom: [
    'Draft an urgent outreach about a time-sensitive opportunity at this account',
    'Write a message for a situation not covered by the standard outreach types',
    'Draft a response to an unexpected inbound inquiry from this contact',
    'Write an outreach for a re-engagement after a long gap with no contact',
  ],
};

// Suggestions per tab, shown when no outreach type applies.
export const STARTER_PROMPTS = {
  compose: [],
  cadence: [
    'Build a 4-touch cadence for a cold pursuit at a target account',
    'Create a follow-up sequence after an initial discovery call',
    'Plan a cadence for re-engaging a stalled opportunity',
    'Build a post-proposal sequence to stay top of mind',
    'Design a warm outreach sequence using a recent signal',
  ],
  library: [
    'Show me best practices for first touch emails in our market',
    'What subject lines work best for executive outreach?',
    'Give me 5 strong opening lines for an account with new leadership',
    'What are the most effective follow-up angles for our kind of sale?',
    'How to reference a signal we picked up without being obvious',
  ],
  coach: [
    'Review this draft and tell me what to improve',
    'What are the most common mistakes in executive outreach?',
    'How should I adjust my tone for a finance buyer vs an operations buyer?',
    'Is my subject line too salesy?',
    'How do I make this email shorter without losing the key message?',
  ],
  content: [
    'Write a LinkedIn article on a change our market is not ready for',
    'Write a POV post on the real cost of delaying a decision in our category',
    'Write an industry take on a talent or capacity problem our buyers feel',
    'Write a thought leadership piece on where our market is heading',
    'Write a LinkedIn post positioning us on the latest trend in our market',
  ],
};
